import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useSEO } from "../hooks/useSEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Brain, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { PasswordInput } from "../components/PasswordInput";

export default function LoginPage() {
  const { login, t, language, appConfig } = useApp();
  useSEO({
    title: language === "es" ? "Iniciar sesión · BScanIA" : "Sign in · BScanIA",
    description: language === "es"
      ? "Entra a tu cuenta de BScanIA — tu mentor personal con IA para decisiones difíciles."
      : "Sign in to BScanIA — your personal AI mentor for hard decisions.",
    canonical: "https://bscan.app/login",
    lang: language,
  });
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Pre-checked if the user accepted the same reminder on this device within the
  // last 30 days. Refreshes every 30 days to keep legal consent fresh (Stripe-style).
  const [acceptTerms, setAcceptTerms] = useState(() => {
    try {
      const ts = localStorage.getItem("bscan_login_terms_accepted_at");
      if (!ts) return false;
      const acceptedAt = parseInt(ts, 10);
      if (!Number.isFinite(acceptedAt)) return false;
      const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
      return Date.now() - acceptedAt < THIRTY_DAYS_MS;
    } catch {
      return false;
    }
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptTerms) {
      toast.error(language === "es"
        ? "Debes confirmar el recordatorio para continuar."
        : "You must confirm the reminder to continue.");
      return;
    }
    // Sanitize inputs to handle iPhone/Safari quirks (auto-capitalization, autofill spaces, etc.)
    const cleanEmail = (email || "").trim().toLowerCase();
    const cleanPassword = (password || "").trim();
    if (!cleanEmail) {
      toast.error(language === "es" ? "Email vacío" : "Empty email");
      return;
    }
    setLoading(true);
    try {
      const u = await login(cleanEmail, cleanPassword);
      try { localStorage.setItem("bscan_login_terms_accepted_at", String(Date.now())); } catch { /* no-op */ }
      toast.success(t("auth.welcome_back"));
      if (!u.questionnaire_completed) navigate(appConfig?.beta_mode ? "/quick-start" : "/onboarding");
      else navigate("/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Decode common backend errors and give user-friendly, actionable hints
      const detailLower = (typeof detail === "string" ? detail : "").toLowerCase();
      let userMsg;
      if (status === 404 || detailLower.includes("user not found") || detailLower.includes("no encontrado")) {
        userMsg = language === "es"
          ? `Email no registrado. ¿Quizás te registraste con otro? Verifica o regístrate aquí.`
          : `Email not registered. Did you sign up with another? Please verify or register.`;
      } else if (status === 401 || detailLower.includes("invalid") || detailLower.includes("incorrect") || detailLower.includes("incorrecta")) {
        userMsg = language === "es"
          ? "Contraseña incorrecta. Toca '¿Olvidaste?' arriba para restablecerla."
          : "Wrong password. Tap 'Forgot?' above to reset it.";
      } else if (status === 0 || !status) {
        userMsg = language === "es"
          ? "Sin conexión. Verifica tu internet o desactiva el bloqueador de anuncios para bscan.app."
          : "No connection. Check your internet or disable ad-blocker for bscan.app.";
      } else {
        userMsg = (typeof detail === "string" && detail) || t("auth.login_failed");
      }
      toast.error(userMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#7C3AED]/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-10" data-testid="login-logo">
          <img
            src="/brand-logo.png"
            alt="BScanIA"
            className="h-28 w-auto"
          />
        </Link>

        <div className="bg-[#121214] border border-white/10 rounded-2xl p-8">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#006CFF] mb-2">{t("auth.login_tag")}</div>
          <h1 className="font-display font-bold text-3xl mb-1">{t("auth.login_title")}</h1>
          <p className="text-white/60 mb-8">{t("auth.login_subtitle")}</p>

          <GoogleSignInButton />

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{t("auth.or_divider")}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-white/80 mb-2 block">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                inputMode="email"
                autoComplete="username"
                className="bg-[#0A0A0A] border-white/15 text-white focus:border-[#006CFF]"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password" className="text-white/80">{t("auth.password")}</Label>
                <Link
                  to="/forgot-password"
                  className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#006CFF] hover:text-white transition-colors"
                  data-testid="login-forgot-password-link"
                >
                  {language === "es" ? "¿Olvidaste?" : "Forgot?"}
                </Link>
              </div>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="flex h-10 w-full rounded-md border border-white/15 bg-[#0A0A0A] px-3 py-2 text-base text-white focus:border-[#006CFF] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                data-testid="login-password-input"
                testIdToggle="login-password-toggle"
              />
            </div>

            {/* Legal reminder — mirrors the register page's policy card. Checkbox required
                to submit, same UX pattern as registration. */}
            <div className="p-4 rounded-xl bg-[#FFB800]/8 border border-[#FFB800]/25" data-testid="login-policy-card">
              <label className="flex items-start gap-3 cursor-pointer select-none" htmlFor="loginAcceptTerms">
                <input
                  id="loginAcceptTerms"
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  required
                  className="mt-1 w-4 h-4 accent-[#006CFF] cursor-pointer flex-shrink-0"
                  data-testid="login-accept-terms-checkbox"
                />
                <div className="flex-1 text-xs leading-relaxed text-white/75">
                  <div className="flex items-center gap-1.5 mb-1.5 text-[#FFB800] font-mono uppercase tracking-[0.15em] text-[10px]">
                    <ShieldAlert className="w-3.5 h-3.5" strokeWidth={2} />
                    {language === "es" ? "Recordatorio — léelo" : "Reminder — please read"}
                  </div>
                  {language === "es" ? (
                    <>
                      Al continuar reafirmas los{" "}
                      <Link to="/terms" target="_blank" className="text-[#006CFF] underline hover:text-[#3388FF]">Términos de Servicio</Link>,
                      la{" "}
                      <Link to="/privacy" target="_blank" className="text-[#006CFF] underline hover:text-[#3388FF]">Política de Privacidad</Link>,
                      y entiendes que <strong className="text-white">BScanIA NO es terapia, ni atención médica, ni asesoría profesional</strong>.
                      Es una herramienta de orientación con IA. Si estás en crisis, llama a una línea de emergencia (en EE.UU.: 988).
                    </>
                  ) : (
                    <>
                      By continuing you reaffirm the{" "}
                      <Link to="/terms" target="_blank" className="text-[#006CFF] underline hover:text-[#3388FF]">Terms of Service</Link>,
                      the{" "}
                      <Link to="/privacy" target="_blank" className="text-[#006CFF] underline hover:text-[#3388FF]">Privacy Policy</Link>,
                      and you understand that <strong className="text-white">BScanIA is NOT therapy, medical care, or professional advice</strong>.
                      It's an AI-powered guidance tool. If you're in crisis, call an emergency line (US: 988).
                    </>
                  )}
                </div>
              </label>
            </div>

            <Button type="submit" disabled={loading || !acceptTerms} className="w-full bg-[#006CFF] hover:bg-[#0052CC] text-white py-6 disabled:opacity-50" data-testid="login-submit-btn">
              {loading ? t("common.loading") : t("auth.login_btn")}
            </Button>
          </form>

          <p className="mt-6 text-center text-white/60 text-sm">
            {t("auth.no_account")}{" "}
            <Link to="/register" className="text-[#006CFF] hover:underline" data-testid="login-to-register-link">
              {t("auth.sign_up")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
